import { useAuth } from '../contexts/AuthContext';
import { useCanWriteCompanySettings } from './useCanWriteCompanySettings';

/**
 * Which /finance/invoices controls this user may be shown.
 *
 * One flag per API gate, not one rule for the screen (the fe#691 pattern).
 * Read from invoices.routes.ts and quickbooks.routes.ts:
 *
 *   reading                 GET /invoices, GET /invoices/summary/dashboard
 *                           requireRole('admin', 'office_staff', 'supervisor',
 *                           'dispatcher'). Nothing here hides data.
 *   canCreate               POST /invoices — requireRole('admin',
 *                           'office_staff'), non-strict, so the elevated
 *                           dispatcher passes.
 *   canSend                 POST /invoices/:id/send — same set.
 *   canRecordPayment        POST /invoices/:id/payment — same set.
 *   canEdit                 PUT /invoices/:id — same set. The editor, the
 *                           detail screen's draft -> finalized Save and its
 *                           recipient pencil all write through it.
 *   canPaymentLink          POST /invoices/:id/payment-link — same set.
 *   canDuplicate            POST /invoices/:id/duplicate — same set.
 *   canCreditNote           POST /invoices/:id/credit-note — same set.
 *   canVoid                 POST /invoices/:id/void — requireRole('admin',
 *                           { strict: true }). Admin alone: office staff and
 *                           the elevated dispatcher are both refused.
 *   canPushQbo              POST /quickbooks/push-invoice/:id —
 *                           requireRole('admin', 'office_staff', { strict: true }).
 *                           Strict opts OUT of dispatcher elevation, so this
 *                           is the one control an elevated dispatcher is not
 *                           shown.
 *   canExportCsv            POST /accounting/invoices/export —
 *                           requireRole('admin', 'office_staff'), NON-strict,
 *                           so the API would also admit an elevated
 *                           dispatcher. Shown to the same literal admin /
 *                           office set as the QuickBooks push on purpose: it
 *                           is the push's offline twin, and the office that
 *                           owns the books is who it is for.
 *
 * Separate fields even where they agree today: they are separate API gates,
 * and one moving must not silently move another. Not `isAdminRole` — it admits
 * `owner`, which none of these lists contain.
 */
export interface InvoicePermissions {
  canCreate: boolean;
  canSend: boolean;
  canRecordPayment: boolean;
  canEdit: boolean;
  canPaymentLink: boolean;
  canDuplicate: boolean;
  canCreditNote: boolean;
  canVoid: boolean;
  canPushQbo: boolean;
  canExportCsv: boolean;
}

export function useInvoicePermissions(): InvoicePermissions {
  const { user } = useAuth();
  const role = user?.role;
  const adminOrElevated = useCanWriteCompanySettings();
  const adminOrOffice = adminOrElevated || role === 'office_staff';
  const literalAdminOrOffice = role === 'admin' || role === 'office_staff';

  return {
    canCreate: adminOrOffice,
    canSend: adminOrOffice,
    canRecordPayment: adminOrOffice,
    canEdit: adminOrOffice,
    canPaymentLink: adminOrOffice,
    canDuplicate: adminOrOffice,
    canCreditNote: adminOrOffice,
    canVoid: role === 'admin',
    canPushQbo: literalAdminOrOffice,
    canExportCsv: literalAdminOrOffice,
  };
}
